"use client";

import React, { useEffect, useState, useCallback } from "react";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Separator } from "./ui/separator";
import AddressNetworkSelector from "./AddressNetworkSelector";
import {
  createWalletAddress,
  updateWalletAddress,
} from "../services/settingsService";
import { Info, ChevronDown, Check, PlusCircle } from "lucide-react";
import ErrorState from "./ErrorState";
import { SecuredFooter } from "./ui/secured-footer";
import loaderVideo from "../assets/images/loader.mp4";
import loaderLogo from "../assets/images/80eight/88-footer-white.svg";
import paySuccessIcon from "../assets/icons/pay_success_icon.svg";

const TRANSFER_TYPES = [
  { id: "80eight", label: "Digital Wallet" },
  { id: "Self Hosted", label: "Bank Transfer" },
  { id: "Exchange", label: "Exchange" },
];

const SERVICE_PROVIDERS = [
  { id: "AltCoinTrader", name: "AltCoinTrader" },
  { id: "Binance", name: "Binance" },
  { id: "BingX", name: "BingX" },
  { id: "Bitget", name: "Bitget" },
  { id: "Bitpanda", name: "Bitpanda" },
  { id: "BloFin", name: "BloFin" },
  { id: "Bybit", name: "Bybit" },
  { id: "Coinbase", name: "Coinbase" },
  { id: "Coinmama", name: "Coinmama" },
  { id: "Coinpass", name: "Coinpass" },
  { id: "Crypto.com", name: "Crypto.com" },
  { id: "eToro", name: "eToro" },
  { id: "Gate.io", name: "Gate.io" },
  { id: "Gemini", name: "Gemini" },
  { id: "Kraken", name: "Kraken" },
  { id: "Kriptomat", name: "Kriptomat" },
  { id: "KuCoin", name: "KuCoin" },
  { id: "Luno", name: "Luno" },
  { id: "MEXC", name: "MEXC" },
  { id: "OKX", name: "OKX" },
  { id: "PrimeXBT", name: "PrimeXBT" },
  { id: "VALR", name: "VALR" },
  { id: "Yellow Card", name: "Yellow Card" },
];

const AddWallet = ({
  setPageNo,
  setRefresh,
  updateUser,
  action,
  onAddAnother,
  initialContactType,
}) => {
  const [contactType, setContactType] = useState(
    initialContactType ?? "individual"
  );
  const [name, setName] = useState("");
  const [lastname, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [transferType, setTransferType] = useState("80eight");
  const [serviceProvider, setServiceProvider] = useState("");
  const [showProviderDropdown, setShowProviderDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFilled, setIsFilled] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [failedMessage, setFailedMessage] = useState("");

  // Single address
  const [selectedAsset, setSelectedAsset] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [walletNo, setWalletNo] = useState("");

  // Bank Transfer fields
  const [recipientBank, setRecipientBank] = useState("");
  const [iban, setIban] = useState("");
  const [swiftCode, setSwiftCode] = useState("");

  // Validation errors
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    // Validate name fields
    if (contactType === "corporate") {
      if (!businessName.trim()) {
        newErrors.businessName = "Business name is required";
      }
    } else {
      if (!name.trim()) {
        newErrors.name = "Name is required";
      }
      if (!lastname.trim()) {
        newErrors.lastname = "Surname is required";
      }
    }

    // Validate asset and network for all transfer types
    if (!selectedAsset || !selectedAsset.assetId) {
      newErrors.asset = "Please select an asset";
    }
    if (!selectedNetwork || (!selectedNetwork._id && !selectedNetwork.id)) {
      newErrors.network = "Please select a network";
    }

    // Validate address fields based on transfer type
    if (transferType === "80eight" || transferType === "Exchange") {
      if (!walletNo.trim()) {
        newErrors.walletNo = "Wallet address is required";
      }
    }

    if (transferType === "Self Hosted") {
      if (!iban.trim()) {
        newErrors.iban = "IBAN is required";
      }
    }

    if (transferType === "Exchange" && !serviceProvider) {
      newErrors.serviceProvider = "Please select a service provider";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  async function createWalletAddressCall() {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const categoryValue =
        contactType === "individual" ? "Individual" : "Corporate";
      const address = {
        firstName: contactType === "corporate" ? businessName : name,
        lastName: contactType === "corporate" ? businessName : lastname,
        addressName:
          contactType === "corporate" ? businessName : `${name} ${lastname}`,
        walletAddress: transferType === "Self Hosted" ? iban : walletNo,
        asset: selectedAsset?.assetId,
        assetName: selectedAsset?.assetName || selectedAsset?.asset,
        chain: selectedNetwork?._id || selectedNetwork?.id,
        chainName: selectedNetwork?.chain,
        walletType: transferType,
        category: categoryValue,
        provider: transferType === "Exchange" ? serviceProvider : undefined,
      };

      const apiCall = await createWalletAddress(address);

      if (!apiCall || (apiCall.status !== 200 && apiCall.status !== 201)) {
        throw new Error("Failed to create address");
      }

      setRefresh(true);
      setIsLoading(false);
      setShowSuccess(true);
    } catch (err) {
      setIsLoading(false);
      setFailedMessage(
        err.response?.data?.message || err.message || "Failed to add contact"
      );
      setShowFailed(true);
    }
  }

  async function updateWalletAddressCall() {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const categoryValue =
      contactType === "individual" ? "Individual" : "Corporate";
    const address = {
      id: updateUser?._id,
      firstName: contactType === "corporate" ? businessName : name,
      lastName: contactType === "corporate" ? businessName : lastname,
      addressName:
        contactType === "corporate" ? businessName : `${name} ${lastname}`,
      walletAddress: transferType === "Self Hosted" ? iban : walletNo,
      asset: selectedAsset?.assetId,
      assetName: selectedAsset?.assetName || selectedAsset?.asset,
      chain: selectedNetwork?._id || selectedNetwork?.id,
      chainName: selectedNetwork?.chain,
      walletType: transferType,
      category: categoryValue,
      provider: transferType === "Exchange" ? serviceProvider : undefined,
    };

    try {
      const apiCall = await updateWalletAddress(address);
      if (apiCall.status === 200) {
        setRefresh(true);
        setIsLoading(false);
        setShowSuccess(true);
      }
    } catch (err) {
      setIsLoading(false);
      setFailedMessage(
        err.response?.data?.message || "Failed to update contact"
      );
      setShowFailed(true);
    }
  }

  const handleTryAgain = () => {
    setShowFailed(false);
    setFailedMessage("");
  };

  const FilledCheck = useCallback(() => {
    const nameFieldsFilled =
      contactType === "corporate"
        ? businessName !== ""
        : name !== "" && lastname !== "";

    // Asset and network are required for all transfer types
    const assetNetworkFilled =
      selectedAsset &&
      selectedAsset.assetId &&
      selectedNetwork &&
      (selectedNetwork._id || selectedNetwork.id);

    let transferFieldsFilled = false;

    if (transferType === "80eight") {
      transferFieldsFilled = assetNetworkFilled && walletNo !== "";
    } else if (transferType === "Self Hosted") {
      transferFieldsFilled = assetNetworkFilled && iban !== "";
    } else if (transferType === "Exchange") {
      transferFieldsFilled =
        assetNetworkFilled && walletNo !== "" && serviceProvider !== "";
    }

    setIsFilled(nameFieldsFilled && transferFieldsFilled);
  }, [
    contactType,
    businessName,
    name,
    lastname,
    walletNo,
    selectedAsset,
    selectedNetwork,
    transferType,
    serviceProvider,
    iban,
  ]);

  useEffect(() => {
    if (
      action === "update" &&
      selectedAsset === "" &&
      selectedNetwork === "" &&
      updateUser != null
    ) {
      // Check if it's a corporate contact
      const isCorporate = updateUser.category === "Corporate";
      if (isCorporate) {
        setContactType("corporate");
        setBusinessName(updateUser.firstName || "");
      } else {
        setContactType("individual");
        setName(updateUser.firstName || "");
        setLastName(updateUser.lastName || "");
      }
      // For Self Hosted, walletAddress is stored as IBAN
      if (updateUser.walletType === "Self Hosted") {
        setIban(updateUser.walletAddress || "");
      } else {
        setWalletNo(updateUser.walletAddress || "");
      }
      // Set asset as object with assetId for validation compatibility
      if (updateUser.asset) {
        const assetObj =
          typeof updateUser.asset === "object"
            ? {
                assetId: updateUser.asset._id || updateUser.asset.assetId,
                assetName: updateUser.assetName || updateUser.asset.assetName,
                asset: updateUser.asset.asset || "",
                assetIcon: updateUser.assetIcon || updateUser.asset.assetIcon,
              }
            : { assetId: updateUser.asset, assetName: updateUser.assetName };
        setSelectedAsset(assetObj);
      }
      // Set network as object with _id for validation compatibility
      if (updateUser.chain) {
        const networkObj =
          typeof updateUser.chain === "object"
            ? updateUser.chain
            : { _id: updateUser.chain, chain: updateUser.chainName };
        setSelectedNetwork(networkObj);
      }
      if (updateUser.walletType) setTransferType(updateUser.walletType);
      if (updateUser.provider) setServiceProvider(updateUser.provider);
      // Bank Transfer fields
      if (updateUser.recipientBank) setRecipientBank(updateUser.recipientBank);
      if (updateUser.iban) setIban(updateUser.iban);
      if (updateUser.swiftCode) setSwiftCode(updateUser.swiftCode);
    }
  }, [action, selectedAsset, selectedNetwork, updateUser]);

  useEffect(() => {
    FilledCheck();
  }, [FilledCheck]);

  const handleDone = () => {
    setPageNo(0);
  };

  // Loading Screen
  if (isLoading) {
    return (
      <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-hidden">
        <CardContent className="p-0">
          <div className="relative w-full h-[600px] flex items-center justify-center">
            <div className="relative w-full max-w-[500px] aspect-[45/32]">
              <video
                src={loaderVideo}
                autoPlay
                loop
                muted
                playsInline
                className="h-full w-full object-contain"
              />
              <img
                src={loaderLogo}
                alt="80eight"
                className="absolute inset-0 z-10 m-auto w-[160px] h-auto"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Failed Screen
  if (showFailed) {
    return (
      <ErrorState
        title={action === "update" ? "Update failed" : "Adding contact failed"}
        message={failedMessage}
        onTryAgain={handleTryAgain}
      />
    );
  }

  // Success Screen
  if (showSuccess) {
    return (
      <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-visible">
        <CardContent className="p-0">
          <div className="flex flex-col items-center justify-center py-20 px-8">
            {/* Success Icon */}
            <div className="mb-12">
              <img
                src={paySuccessIcon}
                alt="Success"
                className="w-[194px] h-[136px]"
              />
            </div>

            {/* Title */}
            <h2 className="text-[32px] font-semibold text-[#33125F] mb-6 text-center leading-[40px]">
              Contact Added
            </h2>

            {/* Subtitle */}
            <p className="text-[20px] text-[#423E51] text-center mb-10 leading-[32px] max-w-[338px]">
              Your contact has been saved successfully.
            </p>

            {/* Done Button */}
            <Button
              onClick={handleDone}
              className="h-[48px] w-[302px] rounded-full text-[14px] font-semibold text-white"
              style={{
                background:
                  "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
              }}
            >
              Done
            </Button>
          </div>
          <SecuredFooter />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-visible">
      <CardContent className="p-0">
        {/* Header */}
        <div className="p-8 pb-0">
          <h2 className="text-[20px] font-semibold text-[#33125F] mb-6">
            {action === "update" ? "Edit contact" : "Add a new contact"}
          </h2>

          {/* Info Banner */}
          <div className="bg-[#F5F3F8] rounded-full px-6 py-4 flex items-center gap-3 mb-6">
            <Info className="w-5 h-5 text-[#57249C] flex-shrink-0" />
            <p className="text-[14px] text-[#6D6584]">
              This information is required for compliance with global Travel
              Rule regulations.
            </p>
          </div>

          {/* Individual / Corporate Toggle */}
          <div className="w-full h-[64px] p-2 bg-transparent border border-[#EBE9EF] rounded-full flex mb-6">
            <button
              onClick={() => setContactType("individual")}
              className={`flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold transition-colors ${
                contactType === "individual"
                  ? "bg-[#33125F] text-white"
                  : "text-[#6D6584]"
              }`}
            >
              Individual
            </button>
            <button
              onClick={() => setContactType("corporate")}
              className={`flex-1 h-[48px] rounded-full lg:text-lg text-[14px] font-semibold transition-colors ${
                contactType === "corporate"
                  ? "bg-[#33125F] text-white"
                  : "text-[#6D6584]"
              }`}
            >
              Corporate
            </button>
          </div>
        </div>

        {/* Form Fields */}
        <div className="px-8 pb-6">
          {/* Asset & Wallet Address Row */}
          <div
            className={`grid ${transferType === "Self Hosted" ? "grid-cols-1" : "grid-cols-3"} gap-4 mb-6`}
          >
            <div className="col-span-1">
              <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                Select asset
              </p>
              <AddressNetworkSelector
                setSelectedAsset={setSelectedAsset}
                setSelectedNetwork={setSelectedNetwork}
                selectedAsset={selectedAsset}
                selectedNetwork={selectedNetwork}
                action={action}
                FilledCheck={FilledCheck}
              />
              {errors.asset && (
                <p className="text-red-500 text-[12px] mt-1">{errors.asset}</p>
              )}
            </div>
            {transferType !== "Self Hosted" && (
              <div className="col-span-2">
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Wallet address
                </p>
                <input
                  type="text"
                  placeholder="Enter wallet address"
                  value={walletNo}
                  onChange={(e) => {
                    setWalletNo(e.target.value);
                    if (errors.walletNo) setErrors({ ...errors, walletNo: "" });
                  }}
                  className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                    errors.walletNo ? "border-red-500" : "border-[#CECBD7]"
                  }`}
                />
                {errors.walletNo && (
                  <p className="text-red-500 text-[12px] mt-1">
                    {errors.walletNo}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Name & Surname Row (Individual) / Business Name (Corporate) */}
          {contactType === "corporate" ? (
            <div className="mb-6">
              <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                Business name
              </p>
              <input
                type="text"
                placeholder="Business name"
                value={businessName}
                onChange={(e) => {
                  setBusinessName(e.target.value);
                  if (errors.businessName)
                    setErrors({ ...errors, businessName: "" });
                }}
                className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                  errors.businessName ? "border-red-500" : "border-[#CECBD7]"
                }`}
              />
              {errors.businessName && (
                <p className="text-red-500 text-[12px] mt-1">
                  {errors.businessName}
                </p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Name
                </p>
                <input
                  type="text"
                  placeholder="Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (errors.name) setErrors({ ...errors, name: "" });
                  }}
                  className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                    errors.name ? "border-red-500" : "border-[#CECBD7]"
                  }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-[12px] mt-1">{errors.name}</p>
                )}
              </div>
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Surname
                </p>
                <input
                  type="text"
                  placeholder="Surname"
                  value={lastname}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    if (errors.lastname) setErrors({ ...errors, lastname: "" });
                  }}
                  className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                    errors.lastname ? "border-red-500" : "border-[#CECBD7]"
                  }`}
                />
                {errors.lastname && (
                  <p className="text-red-500 text-[12px] mt-1">
                    {errors.lastname}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Transfer Type */}
          <div className="mb-6">
            <p className="text-[14px] text-[#6D6584] font-normal mb-3">
              Transfer type
            </p>
            <div className="flex items-center gap-6">
              {TRANSFER_TYPES.map((tt) => (
                <button
                  key={tt.id}
                  type="button"
                  onClick={() => setTransferType(tt.id)}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                      transferType === tt.id
                        ? "border-[#33125F]"
                        : "border-[#CECBD7]"
                    }`}
                  >
                    {transferType === tt.id && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#33125F]" />
                    )}
                  </div>
                  <span className="text-[16px] font-semibold text-[#6D6584]">
                    {tt.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Bank Transfer Fields */}
          {transferType === "Self Hosted" && (
            <div className="space-y-4 mb-6">
              <div>
                <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                  Recipient bank
                </p>
                <input
                  type="text"
                  placeholder="Enter recipient bank name"
                  value={recipientBank}
                  onChange={(e) => {
                    setRecipientBank(e.target.value);
                    if (errors.recipientBank)
                      setErrors({ ...errors, recipientBank: "" });
                  }}
                  className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                    errors.recipientBank ? "border-red-500" : "border-[#CECBD7]"
                  }`}
                />
                {errors.recipientBank && (
                  <p className="text-red-500 text-[12px] mt-1">
                    {errors.recipientBank}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                    IBAN
                  </p>
                  <input
                    type="text"
                    placeholder="Enter IBAN"
                    value={iban}
                    onChange={(e) => {
                      setIban(e.target.value);
                      if (errors.iban) setErrors({ ...errors, iban: "" });
                    }}
                    className={`h-[88px] border rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition ${
                      errors.iban ? "border-red-500" : "border-[#CECBD7]"
                    }`}
                  />
                  {errors.iban && (
                    <p className="text-red-500 text-[12px] mt-1">
                      {errors.iban}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                    SWIFT/BIC code (optional)
                  </p>
                  <input
                    type="text"
                    placeholder="Enter SWIFT code"
                    value={swiftCode}
                    onChange={(e) => setSwiftCode(e.target.value)}
                    className="h-[88px] border border-[#CECBD7] rounded-2xl w-full px-6 text-[16px] text-[#423E51] placeholder:text-[#6D6584] focus:outline-none focus:ring-2 focus:ring-[#57249C] transition"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Service Provider (only when Exchange is selected) */}
          {transferType === "Exchange" && (
            <div className="mb-6 relative z-20">
              <p className="text-[14px] text-[#6D6584] font-normal mb-2">
                Select service provider
              </p>
              <div className="relative">
                <button
                  onClick={() => setShowProviderDropdown(!showProviderDropdown)}
                  className={`h-[88px] rounded-2xl w-full px-6 text-[16px] text-left flex items-center justify-between transition ${
                    showProviderDropdown
                      ? "border-2 border-[#57249C]"
                      : "border border-[#CECBD7]"
                  }`}
                >
                  <span
                    className={
                      serviceProvider ? "text-[#33125F]" : "text-[#CECBD7]"
                    }
                  >
                    {serviceProvider
                      ? SERVICE_PROVIDERS.find((p) => p.id === serviceProvider)
                          ?.name
                      : "Select platform"}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      showProviderDropdown
                        ? "rotate-180 text-[#57249C]"
                        : "text-[#6D6584]"
                    }`}
                  />
                </button>
                {showProviderDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#CECBD7] rounded-2xl shadow-lg z-50 max-h-[220px] overflow-y-auto">
                    {SERVICE_PROVIDERS.map((provider) => (
                      <button
                        key={provider.id}
                        onClick={() => {
                          setServiceProvider(provider.id);
                          setShowProviderDropdown(false);
                        }}
                        className="w-full px-6 py-4 text-left text-[16px] text-[#33125F] hover:bg-[#F5F3F8] transition-colors flex items-center justify-between"
                      >
                        {provider.name}
                        {serviceProvider === provider.id && (
                          <Check className="w-5 h-5 text-[#57249C]" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Add Another Address Link */}
          {onAddAnother && (
            <div className="pt-4">
              <button
                type="button"
                onClick={onAddAnother}
                className="inline-flex items-center gap-2 text-[14px] text-[#57249C] font-semibold hover:underline"
              >
                <PlusCircle size={16} />
                Add another address
              </button>
            </div>
          )}
        </div>

        <Separator className="bg-[#EBE9EF]" />

        {/* Action Buttons */}
        <div className="p-8 flex gap-4">
          <Button
            onClick={() => setPageNo(0)}
            className="flex-1 h-[48px] bg-white border border-[#EBE9EF] text-[#6D6584] text-[14px] font-semibold rounded-full hover:bg-[#F5F3F8] transition-colors"
          >
            Cancel
          </Button>
          <Button
            disabled={!isFilled}
            onClick={() => {
              if (action === "update") {
                updateWalletAddressCall();
              } else {
                createWalletAddressCall();
              }
            }}
            className="flex-1 h-[48px] rounded-full text-[14px] font-semibold text-white disabled:opacity-50"
            style={{
              background:
                "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
            }}
          >
            {action === "update" ? "Save Changes" : "Save contact"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AddWallet;
